import { Route, useLocation, Routes, Navigate } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import NotFound from './pages/NotFound';
import Forbidden from './pages/Forbidden';
import LoadingPage from './components/global/LoadingPage';
import Login from './pages/auth/Login';
import ProtectedRoute from './components/global/ProtectedRoute';
import Home from './pages/Home';
import Layout from './layout/Layout';
import Register from './pages/auth/Register';
import Profile from './pages/profile/Profile';
import Settings from './pages/settings/Settings';

// Import modular routes
import hotelMealPlansRoutes from './routes/hotelMealPlansRoutes';
import hotelRatingsRoutes from './routes/hotelRatingsRoutes';
import hotelRoomCategoriesRoutes from './routes/hotelRoomCategoriesRoutes';
import hotelExtrasRoutes from './routes/hotelExtrasRoutes';
import usersRoutes from './routes/usersRoutes';
import rolesRoutes from './routes/rolesRoutes';
import countriesRoutes from './routes/countriesRoutes';
import nationalitiesRoutes from './routes/nationalitiesRoutes';
import paxTypesRoutes from './routes/paxTypesRoutes';
import productTypesRoutes from './routes/productTypesRoutes';
import termOfPaymentsRoutes from './routes/termOfPaymentsRoutes';
import citiesRoutes from './routes/citiesRoutes';
import transportationsRoutes from './routes/transportationsRoutes';
import categoriesRoutes from './routes/categoriesRoutes';
import servicesRoutes from './routes/servicesRoutes';
import packagesRoutes from './routes/packagesRoutes';
import currenciesRoutes from './routes/currenciesRoutes';
import pricingUnitsRoutes from './routes/pricingUnitsRoutes';
import suppliersRoutes from './routes/suppliersRoutes';
import productsRoutes from './routes/productsRoutes';
import programsRoutes from './routes/programsRoutes';
import agentsRoutes from './routes/agentsRoutes';
import airlinesRoutes from './routes/airlinesRoutes';
import airportsRoutes from './routes/airportsRoutes';

// Pages
const RoutesPath = () => {
    const ScrollToTop = () => {
        const { pathname } = useLocation();
        useEffect(() => {
            window.scrollTo(0, 0);
        }, [pathname]);
        return null;
    };

    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<LoadingPage />}>
                <Routes>
                    {/* Auth Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />

                    {/* 403 Forbidden Page */}
                    <Route path="/forbidden" element={<Forbidden />} />

                    {/* Protected Routes */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <Layout />
                            </ProtectedRoute>
                        }>

                        {/* Home - Redirect to dashboard */}
                        <Route index element={<Navigate replace to="dashboard" />} />

                        {/* Dashboard - Available for all authenticated users */}
                        <Route path="dashboard" element={<Home />} />

                        {/* Profile - Available for all authenticated users */}
                        <Route path="profile" element={<Profile />} />

                        {/* Settings - Requires permission */}
                        <Route
                            path="setting"
                            element={
                                <ProtectedRoute requiredPermission="settings_companies">
                                    <Settings />
                                </ProtectedRoute>
                            }
                        />

                        {/* ============================================ */}
                        {/* Modular Routes - Each module in its own file */}
                        {/* ============================================ */}

                        {/* Users Module */}
                        {usersRoutes}

                        {/* Roles Module */}
                        {rolesRoutes}

                        {/* Countries Module */}
                        {countriesRoutes}

                        {/* Nationalities Module */}
                        {nationalitiesRoutes}

                        {/* Pax Types Module */}
                        {paxTypesRoutes}

                        {/* product Types Module */}
                        {productTypesRoutes}

                        {/* Term of Payments Module */}
                        {termOfPaymentsRoutes}

                        {/* Cities Module */}
                        {citiesRoutes}

                        {/* transportations Module */}
                        {transportationsRoutes}

                        {/* Categories Module */}
                        {categoriesRoutes}

                        {/* Services Module */}
                        {servicesRoutes}

                        {/* Packages Module */}
                        {packagesRoutes}

                        {/* Currencies Module */}
                        {currenciesRoutes}

                        {/* pricing Units Module */}
                        {pricingUnitsRoutes}

                        {/* Suppliers Module */}
                        {suppliersRoutes}

                        {/* Products Module */}
                        {productsRoutes}

                        {/* Programs Module */}
                        {programsRoutes}

                        {/* Agents Module */}
                        {agentsRoutes}

                        {/* Airlines Module */}
                        {airlinesRoutes}

                        {/* Airports Module */}
                        {airportsRoutes}

                        {/* Hotel Meal Plans Module */}
                        {hotelMealPlansRoutes}

                        {/* Hotel Ratings Module */}
                        {hotelRatingsRoutes}

                        {/* Hotel Room Categories Module */}
                        {hotelRoomCategoriesRoutes}

                        {/* Hotel Extras Module */}
                        {hotelExtrasRoutes}

                        {/* ============================================ */}
                        {/* Add more modular routes here */}
                        {/* ============================================ */}

                    </Route>
                    {/* End Protected Routes */}

                    {/* 404 Not Found */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </>
    );
};

export default RoutesPath;